import { SimpleTable } from "@asup/simple-table";
import { loadWrapper, PauseCover } from "aic";
import { ReportVariantSelections } from "components/common/tlfItemTable/ReportVariantSelections";
import { HierarchyBrowserContext } from "components/hierarchybrowser/HierarchyBrowserContext";
import { UPSERT_HIERARCHY_TLF_ITEM_INDEX } from "components/hierarchybrowser/interface";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { UPDATE_TLF_ITEM_INDEX } from "contexts/hierarchy/interface";
import { eHierarchy, eHierarchyLevel, eTlfItemType } from "functions/api/enums";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { isHierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { loadHierarchyTlfs } from "functions/api/hierarchy/loadHierarchyTlfs";
import { IRequestStatus } from "functions/api/interface";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { deleteTlf } from "functions/api/tlfItem/deleteTlf";
import { doReload } from "functions/doReload";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ArrowRepeat, ExclamationCircle } from "react-bootstrap-icons";
import { RemoveModal } from "../modals/RemoveModal";
import "./TlfItemTable.css";
import { getTemplateHeaders } from "./functions/getTemplateHeaders";
import { mapTlfItemTableRow } from "./functions/mapTlfItemTableRow";

export interface TlfItemTableProps {
  id: string;
  label?: string;
  searchResults?: TlfItemIndexReadDto[];
  selectReportVariants?: boolean;
  selectedItems?: string[];
  setSelectedItems?: (ret: string[]) => void;
  showDelete?: boolean;
  showExport?: boolean;
  showSearchResults?: boolean;
  showTocSearchResults?: boolean;
  tableClassName?: string;
}

export const TlfItemTableContext = createContext<{
  canDelete: boolean;
  deleteClick?: (id: string, azsolId: string, version: number) => void;
}>({
  canDelete: false,
});

/**
 * Displays TLF items for the current hierarchy, with optional search, selection, export, and deletion controls.
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the table
 * @param props.headers - Optional header configuration for a specialised table view
 * @param props.label - Optional custom label for the table header
 * @param props.searchResults - Optional array of search result items to display
 * @param props.selectReportVariants - Shows independently selectable Master and variant rows
 * @param props.selectedItems - Optional array of selected item IDs
 * @param props.setSelectedItems - Optional function to update selected items
 * @param props.showDelete - Optional. When true, enables delete functionality
 * @param props.showExport - Optional. When true, displays export selection columns
 * @param props.showHeader - Optional visibility override for the table title row
 * @param props.showSearchResults - Optional. When true, displays search results instead of hierarchy items
 * @param props.showTocSearchResults - Optional. When true, displays TOC search results
 * @param props.tableClassName - Optional additional CSS class for a specialised table view
 */
export const TlfItemTable = ({
  id,
  label,
  searchResults = [],
  selectedItems,
  selectReportVariants = false,
  setSelectedItems,
  showDelete = false,
  showExport = false,
  showSearchResults = false,
  showTocSearchResults = false,
}: TlfItemTableProps): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const hbc = useContext(HierarchyBrowserContext);

  // Get hierarchy from the correct context
  const currentSelection = hbc?.state.treeList.find(
    (h) => h.id === hbc.state.currentHierarchy,
  )?.data;
  const currentHierarchy = isHierarchyReadDto(currentSelection) ? currentSelection : null;
  const hierarchy = currentHierarchy ?? hierarchyContext?.hierarchy;

  // Get TLF items from the correct context
  let tlfItems: TlfItemIndexReadDto[] =
    hbc?.state.hierarchyTlfItemIndex?.find((i) => i.hierarchyId === hbc.state.currentHierarchy)
      ?.tlfItemIndex ??
    hierarchyContext?.tlfItemIndex ??
    [];
  // Remove ARS items if the hierarchy does not accept them
  if (hbc && !hierarchyContext?.hierarchy?.arsEnabled)
    tlfItems = tlfItems.filter(
      (t) => t.type !== eTlfItemType.arsListing && t.type !== eTlfItemType.arsTable,
    );

  // #region DELETE
  // Delete request
  const [deleteRequest, setDeleteRequest] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const deleteTlfItem = useCallback(
    async (removeId: string) => {
      const request = await loadWrapper(
        deleteTlf,
        removeId,
        { ...deleteRequest, requestedId: undefined, error: false },
        setDeleteRequest,
        appSettings.backEndUrl,
      );
      /* istanbul ignore else */
      if (request && request.success && hierarchyContext && hierarchyContext.tlfItemIndex) {
        const newItems = hierarchyContext.tlfItemIndex.filter((i) => removeId !== `${i.id}`);
        hierarchyContext.dispatch({
          operation: UPDATE_TLF_ITEM_INDEX,
          tlfItemIndex: newItems,
        });
      }
    },
    [appSettings.backEndUrl, deleteRequest, hierarchyContext],
  );
  // ID to remove
  const [removeId, setRemoveId] = useState<string>("");
  const [removeName, setRemoveName] = useState<string>("");
  // Show remove modal
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  // Trigger modal when a delete button is pressed
  const checkDelete = useCallback((id: string, azsolId: string, version: number) => {
    setRemoveName(`${azsolId}/${version}`);
    setRemoveId(id);
    setShowRemoveModal(true);
  }, []);
  // #endregion DELETE

  // #region REFRESH
  const [loadStatus, setLoadStatus] = useState<IRequestStatus<HierarchyQueryParams>>({
    requesting: false,
    error: false,
  });
  // Refresh TLF items call
  /**
   * Fetches TLF items for the specified hierarchy query parameters.
   *
   * @param hierarchyQueryParams - The parameters defining the hierarchy to fetch TLF items for
   * @param force - Optional flag to force reload, bypassing any cached data
   */
  const getTlfItems = useCallback(
    async (hierarchyQueryParams: HierarchyQueryParams, force?: boolean) => {
      const response = await loadWrapper(
        loadHierarchyTlfs,
        hierarchyQueryParams,
        { ...loadStatus, error: false, requestedId: force ? undefined : loadStatus.requestedId },
        setLoadStatus,
        appSettings.backEndUrl,
      );
      if (response?.success) {
        // Refresh hierarchy browser
        if (hbc && hierarchyQueryParams.hierarchyId) {
          hbc.dispatch({
            operation: UPSERT_HIERARCHY_TLF_ITEM_INDEX,
            nodeId: hierarchyQueryParams.hierarchyId,
            tlfItemIndex: response.data,
          });
        }
        // Refresh current hierarchy
        else {
          hierarchyContext?.dispatch({
            operation: UPDATE_TLF_ITEM_INDEX,
            tlfItemIndex: response.data,
          });
        }
      }
    },
    [appSettings.backEndUrl, hbc, hierarchyContext, loadStatus],
  );
  useEffect(() => {
    if (hbc) {
      const currentHierarchy = hbc.state.treeList.find(
        (h) => h.id === hbc.state.currentHierarchy,
      )?.data;
      const hierarchyQueryParams = {
        hierarchyId: currentHierarchy?.id,
        parentTag: currentHierarchy?.parentTag,
      };
      if (
        loadStatus.requestingId?.hierarchyId !== hbc.state.currentHierarchy &&
        loadStatus.requestedId?.hierarchyId !== hbc.state.currentHierarchy &&
        !loadStatus.error &&
        hierarchyQueryParams.hierarchyId &&
        hierarchyQueryParams.parentTag
      )
        getTlfItems(hierarchyQueryParams);
    } else {
      if (
        hierarchy &&
        loadStatus.requestingId?.hierarchyId !== hierarchy.id &&
        loadStatus.requestedId?.hierarchyId !== hierarchy.id &&
        !loadStatus.error &&
        hierarchyContext &&
        hierarchyContext.tlfItemIndex === null
      )
        getTlfItems({ hierarchyId: hierarchy.id, parentTag: hierarchy.parentTag });
    }
  }, [
    getTlfItems,
    hbc,
    hierarchy,
    hierarchyContext,
    loadStatus.error,
    loadStatus.requestedId?.hierarchyId,
    loadStatus.requestingId?.hierarchyId,
  ]);
  // #endregion REFRESH

  const tableHeader = useMemo(() => {
    if (label) return label;
    else if (hierarchy) {
      return `${hierarchy.name} ${
        ![eHierarchy.TopLevel, eHierarchy.Report].includes(hierarchy.type) &&
        hierarchy.level !== eHierarchyLevel.Corporate
          ? `${hierarchy.level} `
          : ""
      }${hierarchy.type === eHierarchy.Report ? "ToC items" : "templates"}`;
    }
    return "";
  }, [hierarchy, label]);

  return (
    <TlfItemTableContext
      value={{
        canDelete:
          showDelete &&
          hierarchy !== undefined &&
          hierarchy !== null &&
          [eHierarchy.Directory].includes(hierarchy.type),
        deleteClick: checkDelete,
      }}
    >
      <>
        {loadStatus.requesting && <PauseCover text="Loading TLF items..." />}
        {deleteRequest.requesting && <PauseCover text="Deleting TLF item..." />}

        {loadStatus.error ? (
          <div>
            <ExclamationCircle /> Error loading items; you may not have access. Please{" "}
            <button
              className="button-link"
              onClick={doReload}
            >
              refresh
            </button>
            , or contact the relevant Lead.
          </div>
        ) : (
          <>
            <div style={{ position: "relative" }}>
              <ArrowRepeat
                aria-label="Refresh TLF items"
                id={`${id}-refresh`}
                onClick={() => {
                  setLoadStatus({ requesting: false, error: false });
                  /* istanbul ignore else */
                  if (hierarchy) {
                    const hierarchyQueryParams = {
                      hierarchyId: hierarchy?.id,
                      parentTag: hierarchy?.parentTag,
                    };
                    getTlfItems(hierarchyQueryParams, true);
                  }
                }}
                style={{ position: "absolute", top: 0, right: "4px", zIndex: 3 }}
              />
            </div>
          </>
        )}
        {loadStatus.requesting ? null : tlfItems.length === 0 &&
          !(showSearchResults || showTocSearchResults) ? (
          <>
            <h4>{tableHeader}</h4>
            <p>No items in this hierarchy</p>
          </>
        ) : selectReportVariants ? (
          <ReportVariantSelections items={tlfItems} />
        ) : (
          <SimpleTable
            id={id}
            headerLabel={tableHeader}
            tableClassName={`tlf-item-table `.trim()}
            currentSelection={selectedItems}
            data={(showSearchResults || showTocSearchResults ? searchResults : tlfItems).map(
              mapTlfItemTableRow,
            )}
            fields={getTemplateHeaders(
              showSearchResults,
              showTocSearchResults,
              hierarchy?.level ?? eHierarchyLevel.Corporate,
              showExport,
            )}
            filterLabel={"Hide retired/decommissioned"}
            initialFilterSelected
            keyField="id"
            selectable={setSelectedItems !== undefined}
            selectedBackgroundColor="rgba(0, 0, 0, 0.2)"
            setCurrentSelection={(ret) => {
              /* istanbul ignore else */
              if (setSelectedItems)
                setSelectedItems(ret.filter((r) => typeof r === "string") as string[]);
            }}
            showFilter
            showSearch={showSearchResults || showTocSearchResults ? false : true}
          />
        )}
      </>

      <RemoveModal
        beingRemovedName={removeName}
        beingRemovedType={"Template"}
        id={"remove-template-modal"}
        onCancel={() => {
          setShowRemoveModal(false);
        }}
        onOk={async () => {
          const rid = removeId;
          setRemoveId("Deleting...");
          await deleteTlfItem(rid);
          setShowRemoveModal(false);
          setRemoveId("");
          setRemoveName("");
        }}
        show={showRemoveModal}
      />
    </TlfItemTableContext>
  );
};

TlfItemTable.displayName = "TlfItemTable";
